import { Check, Phone, Clock } from "lucide-react";
import { services, fullTaskList } from "@/data/services";
import { site } from "@/data/site";
import PageHeader from "@/components/PageHeader";
import ContactCta from "@/components/ContactCta";

export default function Ydelser() {
    return (
        <div data-testid="page-ydelser">
            <PageHeader
                overline="Ydelser"
                title="Ordentligt håndværk — både store og små opgaver."
                subtitle="Jeg hjælper dagligt kunder i Silkeborg og omegn med alt fra mindre serviceopgaver til store renoveringer. God dialog, fair pris og løsninger, du bliver glad for i længden."
            />

            {/* Full task list — direkte fra Nicklas' egen beskrivelse */}
            <section className="py-14 sm:py-20 bg-white border-b border-[var(--ns-border)]">
                <div className="container-ns">
                    <div className="max-w-3xl mb-10">
                        <div className="overline mb-4">
                            Det kan jeg hjælpe med
                        </div>
                        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[1.05] mb-4">
                            Fra små reparationer til totalrenovering.
                        </h2>
                        <p className="text-lg text-[var(--ns-muted)] leading-relaxed">
                            For mig handler det ikke bare om at blive færdig
                            hurtigt, men om at levere ordentligt håndværk, god
                            dialog og løsninger, du bliver glad for i
                            længden.
                        </p>
                    </div>

                    <div
                        data-testid="full-task-list"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
                    >
                        {fullTaskList.map((item, i) => (
                            <div
                                key={i}
                                data-testid={`task-item-${i}`}
                                className="flex items-center gap-3 p-4 rounded-md border border-[var(--ns-border)] bg-[var(--ns-bg)] hover:border-[var(--ns-blue)] hover:bg-white transition"
                            >
                                <span className="w-8 h-8 rounded-full bg-[var(--ns-blue)] text-white grid place-items-center flex-shrink-0">
                                    <Check
                                        className="w-4 h-4"
                                        strokeWidth={3}
                                    />
                                </span>
                                <span className="font-display font-bold text-[15px] text-[var(--ns-text)] leading-tight">
                                    {item}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-6 sm:p-8 rounded-md bg-[var(--ns-blue-soft)] border border-[var(--ns-blue)]/10">
                        <div className="w-12 h-12 rounded-full bg-[var(--ns-blue)] text-white grid place-items-center flex-shrink-0 animate-pulse">
                            <Clock className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <div className="font-display font-extrabold text-lg text-[var(--ns-text)] mb-1">
                                {site.availabilityNote}
                            </div>
                            <p className="text-[var(--ns-muted)] text-sm leading-relaxed">
                                Skriv eller ring — så tager vi en snak og
                                finder en løsning, der passer dig 👌
                            </p>
                        </div>
                        <a
                            href={site.phoneHref}
                            data-testid="ydelser-call-button"
                            className="btn-primary !py-3 !px-5 text-sm whitespace-nowrap"
                        >
                            <Phone className="w-4 h-4" />
                            Ring {site.phone}
                        </a>
                    </div>
                </div>
            </section>

            {/* Service details */}
            <section className="py-16 sm:py-24 bg-[var(--ns-bg)]">
                <div className="container-ns">
                    <div className="max-w-2xl mb-14">
                        <div className="overline mb-4">Fire kerneydelser</div>
                        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[1.05]">
                            Alle detaljer — kategori for kategori.
                        </h2>
                    </div>
                    <div className="space-y-20 sm:space-y-28">
                        {services.map((s, i) => {
                            const Icon = s.icon;
                            const reverse = i % 2 === 1;
                            return (
                                <div
                                    key={s.id}
                                    data-testid={`service-detail-${s.id}`}
                                    id={s.id}
                                    className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                                        reverse ? "lg:[direction:rtl]" : ""
                                    }`}
                                >
                                    <div
                                        className={`lg:col-span-6 ${
                                            reverse ? "lg:[direction:ltr]" : ""
                                        }`}
                                    >
                                        <div className="aspect-[4/3] overflow-hidden rounded-md border border-[var(--ns-border)] bg-white">
                                            <img
                                                src={s.image}
                                                alt={s.title}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                    </div>
                                    <div
                                        className={`lg:col-span-6 ${
                                            reverse ? "lg:[direction:ltr]" : ""
                                        }`}
                                    >
                                        <div className="inline-flex items-center gap-3 mb-5">
                                            <span className="w-12 h-12 rounded-md bg-[var(--ns-blue)] text-white grid place-items-center">
                                                <Icon
                                                    className="w-6 h-6"
                                                    strokeWidth={1.7}
                                                />
                                            </span>
                                            <span className="overline">
                                                0{i + 1} · Ydelse
                                            </span>
                                        </div>
                                        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter text-[var(--ns-text)] mb-5 leading-[1.05]">
                                            {s.title}
                                        </h2>
                                        <p className="text-[var(--ns-muted)] text-lg leading-relaxed mb-6">
                                            {s.description}
                                        </p>
                                        <ul className="space-y-3">
                                            {s.bullets.map((b, bi) => (
                                                <li
                                                    key={bi}
                                                    className="flex items-start gap-3 text-[var(--ns-text)]"
                                                >
                                                    <span className="w-6 h-6 rounded-full bg-[var(--ns-blue-soft)] text-[var(--ns-blue)] grid place-items-center flex-shrink-0 mt-0.5">
                                                        <Check
                                                            className="w-3.5 h-3.5"
                                                            strokeWidth={3}
                                                        />
                                                    </span>
                                                    <span className="text-[15px] leading-relaxed">
                                                        {b}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <ContactCta
                title="Står du med en opgave?"
                subtitle="Skriv eller ring — så tager vi en snak og finder en løsning, der passer dig. Helt uforpligtende."
            />
        </div>
    );
}
